import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Pencil, RotateCcw, Save, X } from 'lucide-react';
import { Drawer } from './ui/Drawer';
import { Pill } from './ui/Pill';
import {
  useGlAccount,
  useGlAccounts,
  useGlJournal,
  useReverseJournal,
  useUpdateGlAccount,
  type GlKind,
} from '../hooks/useLedger';
import { formatDate } from '../lib/format';

const inputCn =
  'w-full px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 ' +
  'bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500';

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <label className="text-[11px] uppercase tracking-wide text-ink-500">{label}</label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

const KIND_TONE: Record<GlKind, 'neutral' | 'accent' | 'success' | 'warn' | 'muted'> = {
  asset: 'accent',
  liability: 'warn',
  equity: 'neutral',
  revenue: 'success',
  expense: 'muted',
};

export function GlAccountDrawer({
  wsId,
  accountId,
  open,
  onOpenChange,
}: {
  wsId: string;
  accountId: string | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useGlAccount(wsId, accountId);
  const accounts = useGlAccounts(wsId);
  const update = useUpdateGlAccount(wsId);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<GlKind>('asset');
  const [parentId, setParentId] = useState<string>('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!data?.account) return;
    setName(data.account.name);
    setKind(data.account.kind);
    setParentId(data.account.parent_id ?? '');
    setIsActive(data.account.is_active);
    setEditing(false);
    setError(null);
  }, [data?.account?.id, open]);

  const onSave = async () => {
    if (!data?.account) return;
    setError(null);
    try {
      await update.mutateAsync({
        id: data.account.id,
        patch: {
          name,
          kind,
          parentId: parentId || null,
          isActive,
        },
      });
      setEditing(false);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Save failed');
    }
  };

  const a = data?.account;
  const b = data?.balance;
  const eligibleParents = (accounts.data ?? []).filter((p) => p.id !== a?.id);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={a ? `${a.code} · ${a.name}` : '…'}
      description={a ? t(`ledger.create.kinds.${a.kind}`) : undefined}
      footer={
        a ? (
          <div className="flex items-center justify-end gap-2">
            {editing ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    if (data?.account) {
                      setName(data.account.name);
                      setKind(data.account.kind);
                      setParentId(data.account.parent_id ?? '');
                      setIsActive(data.account.is_active);
                    }
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
                >
                  <X size={12} />
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={onSave}
                  disabled={update.isPending}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
                >
                  <Save size={12} />
                  {t('common.save')}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
              >
                <Pencil size={12} />
                {t('common.edit')}
              </button>
            )}
          </div>
        ) : null
      }
    >
      {isLoading || !a || !b ? (
        <div className="flex items-center gap-2 text-sm text-ink-500 py-10 justify-center">
          <Loader2 size={14} className="animate-spin" />
          {t('common.loading')}
        </div>
      ) : (
        <div className="space-y-5">
          {error && (
            <div className="text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
              {error}
            </div>
          )}

          <section className="rounded-lg border border-ink-200 dark:border-ink-800 p-3">
            <div className="text-[11px] uppercase tracking-wide text-ink-500 mb-2">
              {t('ledger.balance')}
            </div>
            <div className="grid grid-cols-3 text-sm">
              <div>
                <div className="text-[11px] text-ink-500">{t('ledger.debit')}</div>
                <div className="num font-semibold">{Number(b.debitCents).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[11px] text-ink-500">{t('ledger.credit')}</div>
                <div className="num font-semibold">{Number(b.creditCents).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[11px] text-ink-500">{t('ledger.netDebit')}</div>
                <div className="num font-semibold">{Number(b.netDebitCents).toLocaleString()}</div>
              </div>
            </div>
          </section>

          <section className="grid grid-cols-2 gap-x-4 gap-y-4 text-sm">
            <FormField label={t('ledger.code')}>
              <div className="font-mono text-sm">{a.code}</div>
            </FormField>
            {editing ? (
              <>
                <FormField label={t('ledger.kind')}>
                  <select value={kind} onChange={(e) => setKind(e.target.value as GlKind)} className={inputCn}>
                    {(['asset', 'liability', 'equity', 'revenue', 'expense'] as GlKind[]).map((k) => (
                      <option key={k} value={k}>
                        {t(`ledger.create.kinds.${k}`)}
                      </option>
                    ))}
                  </select>
                </FormField>
                <div className="col-span-2">
                  <FormField label={t('ledger.name')}>
                    <input value={name} onChange={(e) => setName(e.target.value)} className={inputCn} />
                  </FormField>
                </div>
                <div className="col-span-2">
                  <FormField label={t('ledger.parent')}>
                    <select value={parentId} onChange={(e) => setParentId(e.target.value)} className={inputCn}>
                      <option value="">{t('ledger.noParent')}</option>
                      {eligibleParents.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.code} · {p.name}
                        </option>
                      ))}
                    </select>
                  </FormField>
                </div>
                <div className="col-span-2 flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    id="acct-active"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="h-4 w-4 rounded border-ink-300 dark:border-ink-700 accent-accent-600"
                  />
                  <label htmlFor="acct-active">{t('ledger.activeFlag')}</label>
                </div>
              </>
            ) : (
              <>
                <FormField label={t('ledger.kind')}>
                  <Pill tone={KIND_TONE[a.kind]}>{t(`ledger.create.kinds.${a.kind}`)}</Pill>
                </FormField>
                <FormField label={t('ledger.name')}>{a.name}</FormField>
                <FormField label={t('ledger.parent')}>
                  {a.parent_id
                    ? eligibleParents.find((p) => p.id === a.parent_id)?.code ?? '—'
                    : '—'}
                </FormField>
                <FormField label={t('ledger.activeFlag')}>
                  <Pill tone={a.is_active ? 'success' : 'warn'}>
                    {a.is_active ? t('common.active') : t('common.inactive')}
                  </Pill>
                </FormField>
              </>
            )}
          </section>
        </div>
      )}
    </Drawer>
  );
}

export function GlJournalDrawer({
  wsId,
  journalId,
  open,
  onOpenChange,
}: {
  wsId: string;
  journalId: string | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useGlJournal(wsId, journalId);
  const accounts = useGlAccounts(wsId);
  const reverse = useReverseJournal(wsId);
  const [error, setError] = useState<string | null>(null);

  const j = data?.journal;
  const lines = data?.lines ?? [];
  const accountById = new Map((accounts.data ?? []).map((a) => [a.id, a]));

  const debitSum = lines.reduce((s, l) => s + Number(l.debit_cents), 0);
  const creditSum = lines.reduce((s, l) => s + Number(l.credit_cents), 0);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={j ? j.memo ?? j.kind : '…'}
      description={j ? formatDate(j.posted_on, i18n.language) : undefined}
      footer={
        j ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-ink-500">
              {t('ledger.debit')}: <span className="num">{debitSum.toLocaleString()}</span> ·{' '}
              {t('ledger.credit')}: <span className="num">{creditSum.toLocaleString()}</span>
            </div>
            {j.reversed_by ? (
              <Pill tone="muted">{t('ledger.alreadyReversed')}</Pill>
            ) : (
              <button
                type="button"
                onClick={async () => {
                  setError(null);
                  try {
                    await reverse.mutateAsync(j.id);
                  } catch (err: any) {
                    setError(err?.response?.data?.message ?? err?.message ?? 'Reverse failed');
                  }
                }}
                disabled={reverse.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-warn-300 text-warn-600 text-xs font-medium hover:bg-warn-50 dark:hover:bg-warn-900/20 disabled:opacity-50"
              >
                <RotateCcw size={12} />
                {t('ledger.reverse')}
              </button>
            )}
          </div>
        ) : null
      }
    >
      {isLoading || !j ? (
        <div className="flex items-center gap-2 text-sm text-ink-500 py-10 justify-center">
          <Loader2 size={14} className="animate-spin" />
          {t('common.loading')}
        </div>
      ) : (
        <div className="space-y-5">
          {error && (
            <div className="text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
              {error}
            </div>
          )}

          <section className="grid grid-cols-3 gap-3 text-sm">
            <FormField label={t('ledger.journalKind')}>
              <Pill tone="neutral">{j.kind}</Pill>
            </FormField>
            <FormField label={t('ledger.postedOn')}>{formatDate(j.posted_on, i18n.language)}</FormField>
            <FormField label={t('ledger.sourceId')}>
              <span className="font-mono text-xs">{j.source_id?.slice(0, 8) ?? '—'}</span>
            </FormField>
            {j.memo && (
              <div className="col-span-3">
                <FormField label={t('inbox.field.notes')}>{j.memo}</FormField>
              </div>
            )}
          </section>

          <section>
            <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">{t('ledger.lines')}</h3>
            <div className="rounded-lg border border-ink-200 dark:border-ink-800 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-ink-50 dark:bg-ink-900/50 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('ledger.account')}</th>
                    <th className="px-3 py-2 font-medium text-right">{t('ledger.debit')}</th>
                    <th className="px-3 py-2 font-medium text-right">{t('ledger.credit')}</th>
                    <th className="px-3 py-2 font-medium">{t('inbox.field.notes')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-200 dark:divide-ink-800">
                  {lines.map((l) => {
                    const acct = accountById.get(l.account_id);
                    return (
                      <tr key={l.id}>
                        <td className="px-3 py-2">
                          <span className="font-mono text-[10px] text-ink-400 mr-2">
                            {acct?.code ?? '—'}
                          </span>
                          {acct?.name ?? l.account_id.slice(0, 8)}
                        </td>
                        <td className="px-3 py-2 text-right num">
                          {Number(l.debit_cents) > 0 ? Number(l.debit_cents).toLocaleString() : ''}
                        </td>
                        <td className="px-3 py-2 text-right num">
                          {Number(l.credit_cents) > 0 ? Number(l.credit_cents).toLocaleString() : ''}
                        </td>
                        <td className="px-3 py-2 text-ink-500">{l.memo ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </Drawer>
  );
}
